import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchExcursionReport, clearExcursionReport } from '#action/project/excursion-report';
import { ProjectsFilterOptionService } from '#service/autocomplete-service/service';
import { PageContainer, HeaderSection } from '#component/reports/styles';
import { generateYearOptions, generateMonthOptions, getMonthLabel } from './helper';
import {
  parseReportParams,
  buildReportSearch,
  isValidMonth,
  isValidYear
} from './url-params';
import ReportConfig from './report-config';
import ReportResults from './report-results';
import { setDocumentTitle } from '#helper';

const ExcursionReportForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { loading, data, error } = useSelector(s => s.excursionReport, shallowEqual);

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [urlInitialized, setUrlInitialized] = useState(false);
  const [shouldAutoGenerate, setShouldAutoGenerate] = useState(false);

  const initialSearch = useRef(location.search);

  const yearOptions = useMemo(() => generateYearOptions(), []);
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const selectedMonthLabel = useMemo(
    () => getMonthLabel(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  const replaceUrlParams = useCallback(({ projectId, year, month }) => {
    const search = buildReportSearch({ projectId, year, month });

    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search });
    }
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    setDocumentTitle('Excursion Report');
    dispatch(updateBreadcrumb([
      { label: 'Reports', path: '/reports' },
      { label: 'Application Excursion Report' }
    ]));

    return () => {
      dispatch(clearExcursionReport());
    };
  }, [dispatch]);

  useEffect(() => {
    const initFromUrl = async () => {
      const { projectId, year, month } = parseReportParams(initialSearch.current);
      const nextYear = isValidYear(year, yearOptions) ? year : new Date().getFullYear();
      const nextMonth = isValidMonth(month) ? month : new Date().getMonth() + 1;

      setSelectedYear(nextYear);
      setSelectedMonth(nextMonth);

      if (projectId) {
        try {
          const service = new ProjectsFilterOptionService(['title']);
          const options = await service.getOptions(null, {}, String(projectId));
          if (options?.[0]) {
            setSelectedProject(options[0]);
          }
        } catch (initError) {
          console.error(initError);
        }
      }

      setShouldAutoGenerate(Boolean(
        projectId && isValidYear(year, yearOptions) && isValidMonth(month)
      ));
      setUrlInitialized(true);
    };

    initFromUrl();
  }, [yearOptions]);

  useEffect(() => {
    if (!urlInitialized || !shouldAutoGenerate) {
      return;
    }

    if (selectedProject?.data?.id && selectedYear && selectedMonth) {
      dispatch(fetchExcursionReport(selectedProject.data.id, selectedYear, selectedMonth));
      setReportGenerated(true);
      setShouldAutoGenerate(false);
    }
  }, [
    urlInitialized,
    shouldAutoGenerate,
    selectedProject,
    selectedYear,
    selectedMonth,
    dispatch
  ]);

  const handleProjectChange = newValue => {
    setSelectedProject(newValue);
    setReportGenerated(false);
    replaceUrlParams({
      projectId: newValue?.data?.id,
      year: selectedYear,
      month: selectedMonth
    });
  };

  const handleYearChange = event => {
    const year = event.target.value;
    setSelectedYear(year);
    setReportGenerated(false);
    replaceUrlParams({
      projectId: selectedProject?.data?.id,
      year,
      month: selectedMonth
    });
  };

  const handleMonthChange = event => {
    const month = event.target.value;
    setSelectedMonth(month);
    setReportGenerated(false);
    replaceUrlParams({
      projectId: selectedProject?.data?.id,
      year: selectedYear,
      month
    });
  };

  const handleGenerateReport = () => {
    if (selectedProject?.data?.id && selectedYear && selectedMonth) {
      replaceUrlParams({
        projectId: selectedProject.data.id,
        year: selectedYear,
        month: selectedMonth
      });
      dispatch(fetchExcursionReport(selectedProject.data.id, selectedYear, selectedMonth));
      setReportGenerated(true);
    }
  };

  const canGenerate = selectedProject?.data?.id && selectedYear && selectedMonth;

  return (
    <PageContainer>
      <HeaderSection>
        <Typography variant="h4" gutterBottom>
          Application Excursion Report
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Generate a monthly excursion report for a specific project
        </Typography>
      </HeaderSection>

      <ReportConfig
        selectedProject={selectedProject}
        onProjectChange={handleProjectChange}
        yearOptions={yearOptions}
        monthOptions={monthOptions}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        onYearChange={handleYearChange}
        onMonthChange={handleMonthChange}
        onGenerateReport={handleGenerateReport}
        canGenerate={canGenerate}
        loading={loading}
      />

      {error && (
        <Box mb={3}>
          <Typography color="error">
            Error:
            {' '}
            {error}
          </Typography>
        </Box>
      )}

      {reportGenerated && data && (
        <ReportResults
          data={data}
          projectTitle={selectedProject?.data?.title}
          monthLabel={selectedMonthLabel}
        />
      )}
    </PageContainer>
  );
};

export default ExcursionReportForm;
